import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Target, ShoppingBag, MessageSquare, Heart, TrendingUp, Flag, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { guidanceAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const ICONS = { flame: Flame, target: Target, shopping: ShoppingBag, message: MessageSquare, heart: Heart, trending: TrendingUp, flag: Flag, zap: Zap };
const LEVEL_STYLES = {
  fire: 'border-amber-400/30 bg-gradient-to-r from-amber-500/10 to-orange-500/10 text-amber-300',
  active: 'border-emerald-500/20 bg-emerald-500/5 text-emerald-400',
};
const BADGE_COLORS = {
  first_backer: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  diversified: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  portfolio_pro: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  loyal_shopper: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  monthly_momentum: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
};

export default function MomentumStreak() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user) return;
    guidanceAPI.getMomentum()
      .then(r => setData(r.data))
      .catch(() => {});
  }, [user]);

  if (!data || (data.streaks.length === 0 && data.badges.length === 0)) return null;

  return (
    <div className="space-y-3" data-testid="momentum-streak">
      {/* Active streaks */}
      {data.streaks.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Your Momentum</p>
          {data.streaks.map((streak, idx) => {
            const Icon = ICONS[streak.icon] || Flame;
            const style = LEVEL_STYLES[streak.level] || LEVEL_STYLES.active;
            return (
              <motion.div
                key={streak.type}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.08 }}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border ${style}`}
                data-testid={`streak-${streak.type}`}
              >
                <Icon size={14} className={streak.level === 'fire' ? 'animate-pulse' : ''} />
                <span className="text-xs font-semibold">{streak.label}</span>
                {streak.level === 'fire' && <span className="text-[9px] ml-auto opacity-60 font-bold uppercase tracking-wider">on fire</span>}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Achievement badges */}
      {data.badges.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Achievements</p>
          <div className="flex flex-wrap gap-1.5">
            {data.badges.map((badge, idx) => {
              const Icon = ICONS[badge.icon] || Flag;
              const color = BADGE_COLORS[badge.type] || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20';
              return (
                <motion.span
                  key={badge.type}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-[10px] font-semibold ${color}`}
                  data-testid={`badge-${badge.type}`}
                >
                  <Icon size={10} />
                  {badge.label}
                </motion.span>
              );
            })}
          </div>
        </div>
      )}

      {/* Momentum message */}
      {data.streak_score > 0 && (
        <p className="text-[10px] text-zinc-600 italic">{data.message}</p>
      )}
    </div>
  );
}
